import { inject, Injectable, signal } from '@angular/core';
import { DatabaseService, QuerySignal } from './database.service';
import { LoggerService } from './logger.service';
import { SessionStorageService } from './session-storage.service';
import { ToastService } from './toast.service';
import { TranslationService } from './translation.service';

export interface User {
  id: string;
  email: string;
  full_name: string;
  avatar_url?: string;
}

export interface OrganizationUser {
  id: string;
  email: string;
  full_name: string;
  avatar_url?: string;
  role: 'owner' | 'admin' | 'manager' | 'user' | 'viewer';
  created_at: string;
  is_active?: number; // 1 = active, 0 = inactive
}

/**
 * UserService - Manages user data from database
 *
 * Responsibilities:
 * 1. Current authenticated user (loadCurrentUser, currentUser signal)
 * 2. Organization users list (getAll, deactivate, reactivate)
 *
 * Does NOT:
 * - Manage authentication tokens (AuthService responsibility)
 * - Load organization data (OrganizationService responsibility)
 * - Handle sync (CustomSyncService responsibility)
 */
@Injectable({ providedIn: 'root' })
export class UserService {
  private db = inject(DatabaseService);
  private toast = inject(ToastService);
  private translate = inject(TranslationService);
  private logger = inject(LoggerService).getLogger('user-service');
  private sessionStorage = inject(SessionStorageService);

  // Current authenticated user state
  currentUser = signal<User | null>(null);
  private loadedUserId: string | null = null;
  private readonly DEACTIVATION_NOTIFIED_KEY = 'deactivation_notified';

  /**
   * Load current authenticated user from local database
   *
   * @param userId - User ID from JWT token (sub claim)
   * @returns User object or null if not found/deactivated
   *
   * Caching behavior:
   * - Skips DB query if already loaded same userId
   * - Cache invalidated on clearCache() (logout, org switch)
   *
   * Deactivation handling:
   * - Shows toast notification once per session
   * - Sets currentUser to null
   * - Returns null to indicate user cannot access this org
   */
  async loadCurrentUser(userId: string): Promise<User | null> {
    this.logger.debug(`loadCurrentUser() called - userId: ${userId}`);

    // Cache: Skip DB query if already loaded for this user
    if (this.loadedUserId === userId && this.currentUser()) {
      this.logger.debug(`Using cached user data for ${userId}`);
      return this.currentUser();
    }

    try {
      await this.db.init();
      const result = await this.db.exec(
        'SELECT id, email, full_name, avatar_url, is_active FROM users WHERE id = ?',
        [userId],
      );

      if (result && result.length > 0) {
        const user = result[0];

        // Check if user has been deactivated from this organization
        if (user.is_active === 0) {
          this.logger.warn('User has been deactivated from this organization');

          // Only show the toast message once (not on every refresh)
          const hasBeenNotified = this.sessionStorage.getItem(this.DEACTIVATION_NOTIFIED_KEY);
          if (!hasBeenNotified) {
            this.toast.info(this.translate.translate('auth.userDeactivated'), false); // Don't auto-dismiss
            this.sessionStorage.setItem(this.DEACTIVATION_NOTIFIED_KEY, 'true');
          }

          // Clear user state to indicate not authenticated for THIS org
          this.currentUser.set(null);
          this.loadedUserId = null;
          return null;
        }

        const userData: User = {
          id: user.id as string,
          email: user.email as string,
          full_name: user.full_name as string,
          avatar_url: user.avatar_url as string | undefined,
        };

        this.currentUser.set(userData);
        this.loadedUserId = userId;
        this.logger.debug(`User loaded successfully: ${userData.email}`);
        return userData;
      } else {
        // User not in local DB - may not have synced yet
        this.logger.warn('User not found in local database');
        this.currentUser.set(null);
        this.loadedUserId = null;
        return null;
      }
    } catch (error) {
      this.logger.error('Failed to load user from database:', error);
      this.currentUser.set(null);
      this.loadedUserId = null;
      return null;
    }
  }

  /**
   * Clear current user cache
   *
   * Called when:
   * - User logs out
   * - User switches organizations (permissions may differ)
   * - User is deactivated
   */
  clearCache(): void {
    this.logger.debug('Clearing current user cache');
    this.currentUser.set(null);
    this.loadedUserId = null;
    this.sessionStorage.removeItem(this.DEACTIVATION_NOTIFIED_KEY);
  }

  /**
   * Create a reactive query for all users in the current organization
   * @param includeInactive - Whether to include deactivated users (default: false)
   */
  createQuery(includeInactive = false): QuerySignal<OrganizationUser[]> {
    return this.db.createQuery('users', () => this.getAll(includeInactive));
  }

  /**
   * Fetch all users from the local database
   * @param includeInactive - Whether to include deactivated users (default: false)
   */
  async getAll(includeInactive = false): Promise<OrganizationUser[]> {
    try {
      const activeFilter = includeInactive ? '' : 'AND is_active = 1';
      const results = await this.db.exec(
        `SELECT id, email, full_name, avatar_url, role, created_at, is_active
         FROM users
         WHERE deleted_at IS NULL ${activeFilter}
         ORDER BY is_active DESC, created_at ASC`,
      );

      return results as OrganizationUser[];
    } catch (error) {
      this.logger.error('Failed to fetch organization users:', error);
      throw error;
    }
  }

  /**
   * Deactivate a user (soft delete - preserves FK relationships)
   * User can be re-invited later
   */
  async deactivate(userId: string): Promise<void> {
    try {
      // Fetch current user data
      const users = await this.db.exec(
        'SELECT id, email, full_name, avatar_url, oauth_provider, oauth_id, role, is_active, created_at, updated_at, deleted_at FROM users WHERE id = ?',
        [userId],
      );

      if (users.length === 0) {
        throw new Error('User not found');
      }

      const user = users[0] as any;
      const now = new Date().toISOString();

      // Use run() so this admin action is tracked for sync upload to the server
      await this.db.run(
        `INSERT OR REPLACE INTO users (
          id, email, full_name, avatar_url,
          oauth_provider, oauth_id, role,
          is_active, created_at, updated_at, deleted_at
        ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [
          user.id,
          user.email,
          user.full_name,
          user.avatar_url,
          user.oauth_provider,
          user.oauth_id,
          user.role,
          0, // is_active = 0 (deactivated)
          user.created_at,
          now, // updated_at
          user.deleted_at,
        ],
      );

      this.logger.debug('User deactivated:', userId);
    } catch (error) {
      this.logger.error('Failed to deactivate user:', error);
      throw error;
    }
  }

  /**
   * Reactivate a previously deactivated user
   */
  async reactivate(userId: string): Promise<void> {
    try {
      // Fetch current user data
      const users = await this.db.exec(
        'SELECT id, email, full_name, avatar_url, oauth_provider, oauth_id, role, is_active, created_at, updated_at, deleted_at FROM users WHERE id = ?',
        [userId],
      );

      if (users.length === 0) {
        throw new Error('User not found');
      }

      const user = users[0] as any;
      const now = new Date().toISOString();

      // Use run() so this admin action is tracked for sync upload to the server
      await this.db.run(
        `INSERT OR REPLACE INTO users (
          id, email, full_name, avatar_url,
          oauth_provider, oauth_id, role,
          is_active, created_at, updated_at, deleted_at
        ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [
          user.id,
          user.email,
          user.full_name,
          user.avatar_url,
          user.oauth_provider,
          user.oauth_id,
          user.role,
          1, // is_active = 1 (reactivated)
          user.created_at,
          now, // updated_at
          user.deleted_at,
        ],
      );

      this.logger.debug('User reactivated:', userId);
    } catch (error) {
      this.logger.error('Failed to reactivate user:', error);
      throw error;
    }
  }
}
